import { useState, useEffect } from "react"; // <--- Adicionado
import { motion } from "framer-motion";
import { 
  User, ChevronRight, LogOut,
  CreditCard, Star, MessageSquareHeart, Edit3, CheckCircle2, Loader2, Clock, Crown,
  Smartphone, Download // <--- Adicionados
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

// Menu items
const menuItems = [
  { 
    icon: MessageSquareHeart, 
    label: "Deixe um Feedback", 
    desc: "Elogios, críticas ou ideias", 
    to: "/profile/feedback", 
    color: "text-pink-500", 
    bg: "bg-pink-100" 
  },
];

export default function Profile() {
  const navigate = useNavigate();
  const { user, userProfile, logout, loading, trialDaysLeft, isPaidUser, currentPlan, planExpiresAt } = useAuth();

  // --- Lógica para PWA (Detecção de instalação) ---
  const [isInstalled, setIsInstalled] = useState(false);

    useEffect(() => {
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
                          || window.navigator.standalone; // <--- Removido o "(as any)"
        setIsInstalled(isStandalone);
      }, []);

  // Get user display info
  const userName = user?.displayName || userProfile?.name || "Usuário";
  const userEmail = user?.email || "";
  const avatarInitial = (userName && userName.length > 0) 
  ? userName.charAt(0).toUpperCase() 
  : "?";
  const isOnboardingComplete = userProfile?.onboardingCompleted !== false;

  const handleLogout = async () => {
    const result = await logout();
    if (result.success) {
      toast.success("Até logo! Vê se não gasta muito. 💸");
      navigate("/");
    } else {
      toast.error(result.error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  // Mapeamento de nomes amigáveis para os planos
  const planNames = {
    annual: "Troquinho VIP",
    pix_31: "Acesso Avulso",
    semiannual: "Plano Equilíbrio",
    monthly: "Plano Essencial",
    free: "Degustação"
  };

  return (
    <div className="px-6 py-8 pb-32">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <h1 className="text-2xl font-extrabold tracking-tight text-foreground">Meu Perfil</h1>
      </motion.header>

      {/* User Card - Link para Edição de Dados */}
      <Link to="/profile/personal">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bento-card flex items-center gap-4 mb-6 card-interactive cursor-pointer border border-transparent hover:border-primary/20"
          data-testid="user-card"
        >
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-indigo-500 flex items-center justify-center text-white text-2xl font-bold">
            {avatarInitial}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-foreground">{userName}</h2>
              <Edit3 className="w-4 h-4 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground text-sm mb-1">{userEmail}</p>
            
            {isOnboardingComplete && (
              <div className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                <CheckCircle2 className="w-3 h-3" /> Conta Ativa
              </div>
            )}
          </div>
        </motion.div>
      </Link>

{/* Plan Card - Mercado Pago (Versão Slim & Smart) */}
<motion.div
  initial={{ opacity: 0, y: 20 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ delay: 0.15 }}
  className={`bento-card text-white mb-6 relative overflow-hidden ${
    isPaidUser 
      ? "bg-gradient-to-br from-emerald-500 to-teal-600 shadow-md shadow-emerald-100"
      : "bg-gradient-to-br from-amber-400 to-orange-500 shadow-md shadow-orange-100"
  }`}
>
  {/* Detalhe estético de fundo sutil */}
  <div className="absolute top-0 right-0 w-24 h-24 bg-white opacity-10 rounded-full blur-2xl -mr-8 -mt-8"></div>
  
  <div className="flex items-center justify-between relative z-10 gap-4">
    <div className="flex-1">
      <div className="flex items-center gap-2 mb-0.5">
        {isPaidUser ? (
          <Crown className="w-4 h-4 fill-white" />
        ) : (
          <Clock className="w-4 h-4" />
        )}
        <span className="font-black text-[11px] uppercase tracking-wider opacity-90">
          {isPaidUser ? (planNames[currentPlan] || "Plano Premium") : "Degustação Grátis"}
        </span>
      </div>
      
      <div className="flex flex-col">
        <p className="text-[14px] font-bold">
          {isPaidUser 
            ? `Válido até ${planExpiresAt ? planExpiresAt.toLocaleDateString('pt-BR') : '...'}`
            : `${trialDaysLeft} ${trialDaysLeft === 1 ? 'dia restante' : 'dias restantes'}`
          }
        </p>
        
        {/* Aviso discreto se a renovação estiver cancelada */}
        {isPaidUser && userProfile?.willRenew === false && (
          <span className="text-[9px] font-black bg-black/10 w-fit px-1.5 py-0.5 rounded mt-1 uppercase">
            Renovação desativada
          </span>
        )}
      </div>
    </div>

    <Button
      variant="secondary"
      size="sm"
      className="rounded-full font-black text-[11px] h-9 px-5 bg-white text-slate-900 hover:bg-slate-50 transition-all active:scale-95 shadow-sm shrink-0"
      onClick={() => navigate("/profile/payment")}
    >
      {isPaidUser ? "GERENCIAR" : "VER PLANOS"}
    </Button>
  </div>
</motion.div>

      {/* Card de Instalação PWA */}
      {!isInstalled && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <h3 className="text-[10px] font-bold text-muted-foreground px-2 mb-3 uppercase tracking-[0.2em]">Experiência Premium</h3>
          <Link to="/profile/pwa-install">
            <div className="bento-card bg-indigo-50 border-indigo-100 flex items-center justify-between card-interactive !p-4 border-dashed border-2">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-200">
                  <Smartphone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-black text-indigo-900 text-sm flex items-center gap-1">
                    Instalar o Troquinho <span className="bg-indigo-200 text-indigo-700 text-[9px] px-2 py-0.5 rounded-full ml-1 font-bold">DICA</span>
                  </p>
                  <p className="text-xs text-indigo-600/80 font-medium italic">Use como um App na sua tela inicial</p>
                </div>
              </div>
              <Download className="w-5 h-5 text-indigo-400" />
            </div>
          </Link>
        </motion.div>
      )}

      {/* Menu Items */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="space-y-3 mb-8"
      >
        <h3 className="text-sm font-bold text-muted-foreground px-2 capitalize tracking-wider">Feedback</h3>
        
        {menuItems.map((item, index) => (
          <Link key={item.label} to={item.to}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.25 + index * 0.05 }}
              className="bento-card !p-3 flex items-center justify-between card-interactive mb-2"
              data-testid={`menu-${item.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-2xl ${item.bg} flex items-center justify-center shrink-0`}>
                  <item.icon className={`w-5 h-5 ${item.color}`} />
                </div>
                <div>
                  <p className="font-medium text-foreground text-sm">{item.label}</p>
                  <p className="text-xs text-muted-foreground">{item.desc}</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground/50" />
            </motion.div>
          </Link>
        ))}
      </motion.div>

      {/* Logout */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <Button
          variant="outline"
          className="w-full h-14 rounded-2xl font-bold text-destructive border-destructive/20 hover:bg-destructive hover:text-white transition-colors"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5 mr-2" />
          Sair da conta
        </Button>
      </motion.div>

      {/* Footer / Support Disclaimer */}
      <div className="text-center mt-12 space-y-4 px-4">
        <div className="space-y-1">
          <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-bold">
            Tem alguma dúvida ou precisa falar com a gente?
          </p>
          <a 
            href="mailto:contato@apptroquinho.com.br" 
            className="text-sm font-medium text-primary hover:text-primary/80 transition-colors underline-offset-4 underline"
          >
            contato@apptroquinho.com.br
          </a>
        </div>

        <div className="pt-4 border-t border-border/40">
          <p className="text-xs font-medium text-muted-foreground italic">
            "Dinheiro não traz felicidade, mas ajuda a pagar o Wi-Fi."
          </p>
          <p className="text-[10px] text-muted-foreground/40 font-bold mt-2 uppercase">
            Troquinho v1.0.0-beta
          </p>
        </div>
      </div>
    </div>
  );
}
